import type { FC } from "react";
import { memo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { FaEllipsisV, FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { bannerStatusCondition } from "../../constants/enum";
import { ProductLibBanner } from "../../API";

const partImg =
  process.env.REACT_APP_MEDIA_URL ||
  "https://qas-productlibrary-media.neo-corporate.com";

export interface CardProps {
  id: string;
  data: ProductLibBanner;
  moveCard: (draggedId: string, id: string) => void;
  setRemoveId: (id: string) => void;
  cardIsDragging?: boolean;
}

const Card: FC<CardProps> = memo(function Card({
  id,
  data,
  moveCard,
  setRemoveId,
  cardIsDragging,
}) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);
  const [{ isDragging, handlerId }, connectDrag] = useDrag({
    type: "card",
    item: {
      id,
      data,
      moveCard,
      setRemoveId,
    },
    collect: (monitor) => {
      const result = {
        handlerId: monitor.getHandlerId(),
        isDragging: monitor.isDragging(),
      };
      return result;
    },
  });

  const [, connectDrop] = useDrop({
    accept: "card",
    hover({ id: draggedId }: { id: string; type: string }) {
      if (draggedId !== data.id) {
        moveCard(draggedId, data.id);
      }
    },
  });

  connectDrag(ref);
  connectDrop(ref);
  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`
        border border-solid rounded-lg mb-5 p-3 drop-shadow-lg 
        ${isDragging ? "bg-blue-100" : "bg-white"}
        ${cardIsDragging ? "cursor-move" : ""}
        ${isDragging && !cardIsDragging ? "cursor-not-allowed" : ""}
        `}
      onMouseLeave={() => setShowDropdown(false)}
    >
      <div className="flex justify-between m-2">
        <div className="flex gap-5">
          <div className="w-72 h-44">
            {data.path ? (
              <img
                src={`${partImg}/${data.path}`}
                alt="ImageBanner"
                className="object-cover rounded-lg w-full h-full"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/no_img.jpg";
                  e.currentTarget.style.opacity = "0.3";
                }}
              />
            ) : (
              <img
                src={"/no_img.jpg"}
                alt={"ImageBanner"}
                className="object-cover rounded-lg w-full h-full opacity-50"
              />
            )}
          </div>
          <div className="grid grid-rows-2">
            <div>
              <div className={`${bannerStatusCondition[data.status].color}`}>
                {bannerStatusCondition[data.status].text}
              </div>
              <div className="text-lg font-bold">{data.name}</div>
              <div className="font-bold opacity-50">
                {data.periodStart
                  ? format(new Date(data.periodStart), "dd/MM/yyyy")
                  : "Not Find PeriodStart"}{" "}
                -{" "}
                {data.periodEnd
                  ? format(new Date(data.periodEnd), "dd/MM/yyyy")
                  : "ตลอดไป"}{" "}
              </div>
            </div>
            <div className="content-end opacity-50">
              <div className="">{data.updatedBy}</div>
            </div>
          </div>
        </div>
        <div className="justify-items-end">
          <div
            className={`relative ${
              cardIsDragging
                ? "opacity-30 cursor-not-allowed"
                : "cursor-pointer"
            }`}
            // onMouseEnter={() => setShowDropdown(true)}
            onClick={() => {
              if (!cardIsDragging) {
                setShowDropdown((prev) => !prev);
              }
            }}
          >
            <FaEllipsisV className="h-5 w-5" />
            {showDropdown && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg"
                // onMouseLeave={() => setShowDropdown(false)}
              >
                <ul>
                  <li className="px-4 py-2 hover:bg-gray-100">
                    <div
                      className="flex items-center gap-2"
                      onClick={() => navigate(`/banner/${data.id}/update`)}
                    >
                      <FaRegEdit />
                      Edit
                    </div>
                  </li>
                  <li className="px-4 py-2 hover:bg-gray-100">
                    <div
                      className="flex items-center gap-2"
                      onClick={() => setRemoveId(data.id)}
                    >
                      <RiDeleteBin6Line />
                      Delete
                    </div>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Card;
